import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Modal, Spin, message as messager } from "antd";
import { CheckCircleFilled, EditOutlined, FileProtectOutlined } from "@ant-design/icons";
import alioss from "ali-oss";

import SignPad from "../../../../signPad/SignPad";
import {
  DoctorConsentItem,
  getDoctorConsentList,
  syncDoctorConsentSign,
} from "../../../../../services/data/data";
import {
  makeUploadName,
  makeUploadOptions,
} from "../../../../crf_form/components/ocr-autofill/utils";
import { readTenantCode, readUserInfo } from "../../../../../utils/storage";
import modStyl from './ConsentSignModal.module.scss'

// 协议模板中的医生签名占位符
const SIGN_PLACEHOLDER = '${{doctorSignature}}'

const formatSignTime = (d: Date)=> {
  const p = (n: number)=> String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
}

type Props = {
  visible: boolean;
  projectCode: string;
  onClose: ()=> void;
}

/**
 * newCrm=1 项目 - 医生知情同意书签署弹窗
 * 进入首页时触发：先查 /subject/consent/list，外层 signed=true 不弹，false 弹窗签第一条协议
 */
const ConsentSignModal: React.FC<Props> = ({ visible, projectCode, onClose })=> {

  const [checking, setChecking] = useState(true)
  const [needSign, setNeedSign] = useState(false)
  const [doctorName, setDoctorName] = useState('')
  const [consent, setConsent] = useState<DoctorConsentItem | null>(null)
  const [signUrl, setSignUrl] = useState('')
  const [padVisible, setPadVisible] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const tenantCode = useMemo(()=> readTenantCode() || '', [])
  // 登录账号即医生手机号
  const phone = useMemo(()=> (readUserInfo('userCode') as string) || '', [])

  // 用签名图替换模板占位符，生成最终定稿 HTML
  const renderContent = useCallback((html: string, signature: string)=> {
    if (!signature) {
      return html
    }
    const img = `<img src="${signature}" alt="医生签名" style="width:120px;vertical-align:bottom;" />`
    return html.split(SIGN_PLACEHOLDER).join(img)
  }, [])

  const previewHtml = useMemo(()=> {
    if (!consent) {
      return ''
    }
    return renderContent(consent.content, signUrl)
  }, [consent, signUrl, renderContent])

  const check = useCallback(async ()=> {
    if (!visible || !projectCode || !phone) {
      onClose()
      return
    }
    setChecking(true)
    try {
      const res: any = await getDoctorConsentList({ tenantCode, projectCode, phone })
      const data = res?.data
      if (String(res?.code) === '0' && data) {
        if (data.signed) {
          onClose()
          return
        }
        const list = Array.isArray(data.consents)
          ? [...data.consents].sort((a: DoctorConsentItem, b: DoctorConsentItem)=> (a.sort || 0) - (b.sort || 0))
          : []
        if (list.length === 0) {
          onClose()
          return
        }
        setDoctorName(data.doctorName || '')
        setConsent(list[0])
        setNeedSign(true)
      } else {
        // 查询失败（requestPostRes 已提示），不阻塞首页
        onClose()
      }
    } finally {
      setChecking(false)
    }
  }, [visible, projectCode, phone, tenantCode])

  useEffect(()=> {
    if (visible) {
      check()
    }
  }, [visible])

  // 签名图片上传 OSS
  const uploadSignature = async (file: File)=> {
    const pieces = await makeUploadOptions(messager.error)
    if (!pieces) {
      messager.error('初始化上传参数失败')
      return ''
    }
    const [prefix, options] = pieces
    const client = new alioss(options)
    const pathname = makeUploadName(prefix, file)
    if (!pathname) {
      messager.error('上传缺少必要参数')
      return ''
    }
    const result = await client.put(pathname, file, { timeout: 600 * 1000 })
    if (result.res.status !== 200) {
      messager.error('签名上传失败，请重试！')
      return ''
    }
    return result.url
  }

  const onPadConfirm = async (file: File)=> {
    setPadVisible(false)
    const hidden = messager.loading('签名上传中...', 0)
    try {
      const url = await uploadSignature(file)
      if (url) {
        setSignUrl(url)
      }
    } catch (e) {
      messager.error('签名上传失败，请重试！')
    } finally {
      hidden()
    }
  }

  const onSubmit = async ()=> {
    if (!consent || !signUrl || submitting) {
      return
    }
    setSubmitting(true)
    const hidden = messager.loading('正在提交签署结果...', 0)
    try {
      const res: any = await syncDoctorConsentSign({
        tenantCode,
        projectCode,
        phone,
        consentId: consent.consentId,
        content: renderContent(consent.content, signUrl),
        signatureUrl: signUrl,
        signTime: formatSignTime(new Date()),
      })
      if (String(res?.code) === '0') {
        messager.success('签署成功')
        onClose()
      }
    } finally {
      hidden()
      setSubmitting(false)
    }
  }

  return (
    <Modal
      width={760}
      visible={visible && needSign}
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      destroyOnClose
      centered
    >
      <div className={modStyl.wrapper}>
        <div className={modStyl.header}>
          <div className={modStyl.iconWrap}><FileProtectOutlined /></div>
          <div>
            <div className={modStyl.title}>{consent?.title || '知情同意书'}</div>
            <div className={modStyl.sub}>
              请阅读协议内容，确认无误后完成电子签名{doctorName ? `（医生：${doctorName}）` : ''}
            </div>
          </div>
        </div>
        <div className={modStyl.body}>
          {
            checking
              ? <div className={modStyl.loadingWrap}><Spin tip="正在查询签署状态..." /></div>
              : <iframe className={modStyl.ifr} title="知情同意书" srcDoc={previewHtml} />
          }
        </div>
        <div className={modStyl.footer}>
          <div className={modStyl.signStatus}>
            {
              signUrl
                ? <><CheckCircleFilled className={modStyl.okIcon} />签名已完成，请提交</>
                : '阅读完成后，请手写签名并提交'
            }
          </div>
          <div className={modStyl.actions}>
            {
              signUrl ? (
                <>
                  <Button disabled={submitting} onClick={()=> setPadVisible(true)}>重新签名</Button>
                  <Button type="primary" loading={submitting} onClick={onSubmit}>提交签署</Button>
                </>
              ) : (
                <Button type="primary" icon={<EditOutlined />} onClick={()=> setPadVisible(true)}>立即签名</Button>
              )
            }
          </div>
        </div>
      </div>
      {padVisible && <SignPad onConfirm={onPadConfirm} onClose={()=> setPadVisible(false)} />}
    </Modal>
  )
}

export default ConsentSignModal
